import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, Download, FileStack } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Badges";
import { FileTypeBadge, FILE_ICON, downloadForm, FORM_WRITERS } from "@/lib/forms";
import { FormEditorDialog } from "@/components/forms/FormEditorDialog";

const ALL = "all";

const FilterSelect = ({ value, onChange, options, label, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11 rounded-full bg-white md:w-52" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{label}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

function FormCard({ f, writer, onDownload }) {
  const Icon = FILE_ICON[f.file_type];
  return (
    <div className="group flex flex-col rounded-2xl border border-lasso-line bg-white p-5 transition-colors hover:border-lasso-ink" data-testid={`form-card-${f.id}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-lasso-tint text-lasso-deep"><Icon className="h-5 w-5" /></span>
        <div className="flex items-center gap-2">{f.status !== "published" && <StatusBadge status={f.status} />}<FileTypeBadge type={f.file_type} /></div>
      </div>
      <Link to={`/forms/${f.id}`} className="mt-4 font-display text-base font-bold leading-snug hover:text-lasso-deep" data-testid={`form-card-link-${f.id}`}>{f.title}</Link>
      <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">{f.category}</p>
      <p className="mt-3 line-clamp-2 flex-1 text-sm text-zinc-600">{f.description}</p>
      <div className="mt-4 flex items-center justify-between border-t border-lasso-line pt-3 text-xs text-zinc-500">
        <span>v{f.version} · {formatDate(f.updated_at)}{writer && <span data-testid={`form-card-downloads-${f.id}`}> · {f.download_count} downloads · <span data-testid={`form-card-used-${f.id}`}>used {f.times_used ?? 0}×</span></span>}</span>
        <button onClick={() => onDownload(f)} className="inline-flex items-center gap-1.5 rounded-full bg-lasso-ink px-3 py-1.5 font-semibold text-white hover:bg-lasso-navy" data-testid={`form-card-download-${f.id}`}><Download className="h-3.5 w-3.5" /> Download</button>
      </div>
    </div>
  );
}

export default function Forms() {
  const { user } = useAuth();
  const writer = FORM_WRITERS.includes(user.role);
  const [meta, setMeta] = useState({ categories: [], file_types: [] });
  const [forms, setForms] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(ALL);
  const [fileType, setFileType] = useState(ALL);
  const [status, setStatus] = useState("published");
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.get("/forms/meta").then((r) => setMeta(r.data)); }, []);
  const load = useCallback(() => {
    const params = { status };
    if (q.trim()) params.q = q.trim();
    if (category !== ALL) params.category = category;
    if (fileType !== ALL) params.file_type = fileType;
    api.get("/forms", { params }).then((r) => setForms(r.data));
  }, [q, category, fileType, status]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const download = async (f) => {
    try {
      await downloadForm(f);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Library" title="Forms" description="Blank, ready-to-print forms for every part of community operations."
        actions={writer && <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="forms-new-button"><Plus className="h-4 w-4" /> New form</button>} />
      <div className="mb-8 flex flex-col gap-3 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search forms" className="h-11 rounded-full bg-white pl-11" data-testid="forms-search-input" />
        </div>
        <FilterSelect value={category} onChange={setCategory} label="All categories" options={meta.categories.map((c) => ({ value: c, label: c }))} testId="forms-category-filter" />
        <FilterSelect value={fileType} onChange={setFileType} label="All file types" options={meta.file_types.map((t) => ({ value: t, label: t.toUpperCase() }))} testId="forms-type-filter" />
        {writer && (
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 rounded-full bg-white md:w-40" data-testid="forms-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="published">Published</SelectItem><SelectItem value="archived">Archived</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
          </Select>
        )}
      </div>
      {forms?.length === 0 ? <EmptyState icon={FileStack} title="No forms match" testId="forms-empty" /> : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="forms-grid">
          {forms?.map((f) => <FormCard key={f.id} f={f} writer={writer} onDownload={download} />)}
        </div>
      )}
      <FormEditorDialog open={creating} categories={meta.categories} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
    </div>
  );
}
